import { useCallback, useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Tooltip,
} from "@mui/material";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";

import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
// The Last Synced To column renders through JBWDateLabel, which is moment.utc. This picker is
// seeded from the SAME field, so it has to read it in UTC too -- see the fallback below.
import utc from "dayjs/plugin/utc";

import { dealerAdminToolsUpdate } from "../services/dealerServices";
import { updateAutoProcessAgreement } from "../services/tenantServices";
import { AuthContext } from "../AuthContext";
import JbwSwitch from "./JbwSwitch";
import CircularProgress from "@mui/material/CircularProgress";
import JBWDateLabel from "./JBWDateLabel";
import { AdminDealerSearch } from "./AdminDealerSearch";
import CustomNoRowsOverlay from "./gridNoRowOverLay";
import { EXPORT_STATUS_VALUES } from "../utils/userList";

// Applied here rather than beside its import: a statement between imports makes every import
// after it an `import/first` error, which CRA treats as a build failure.
dayjs.extend(utc);

// Turns an axios failure into something an operator can act on. The sync trigger has two
// distinct failure shapes and they need different words: a 400 means this dealer has no sync
// to trigger (its crmSystem maps to no target), which is a permanent fact about the dealer;
// a 502 means the trigger fired and the downstream Lambda threw, which is transient and whose
// cause is in the payload. Collapsing both to "Update failed" would leave the operator
// retrying a button that can never work.
const describeError = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message ?? "Update failed. Please try again.";

  const detail = data.error?.errorMessage ?? data.error?.message;
  if (data.target && detail) return `${data.message} in ${data.target}: ${detail}`;
  if (detail) return `${data.message}: ${detail}`;
  return data.message ?? "Update failed. Please try again.";
};

// Shared by the Sync From picker and its header, so the header's info icon lands on the
// PICKER's right edge rather than the column's. The Sync button sits outside this width, which
// is why the two cannot just both be width:100% -- the column is wider than the field.
const SYNC_PICKER_WIDTH = 160;

const STATUS_OPTIONS = ["Active", "InActive"];
export const DealerAdminTool = () => {
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmPayload, setConfirmPayload] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // { severity, message }. Same Snackbar+Alert shape AgreementSyncGrid.jsx:597-606 uses;
  // this component previously had no user-visible feedback of any kind, only console.log.
  const [notice, setNotice] = useState(null);
  const [allDealersData, setAllDealersData] = useState(false);
  const [datePickerValues, setDatePickerValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [autoProcessLoading, setAutoProcessLoading] = useState({});

  const openConfirmation = useCallback((actionType, payload) => {
    setConfirmAction(actionType); // 'status' | 'stDataPull' | 'lastSyncDate'
    setConfirmPayload(payload); // Contains rowId and newValue and crmSystem
    setConfirmOpen(true);
  }, []);

  const handleConfirmAction = async () => {
    if (!confirmAction || !confirmPayload) return;
    setConfirmLoading(true);
    const { rowId, newValue ,crmSystem,tenantId} = confirmPayload;
    try {
      if (confirmAction === "Status") {
        const result = await dealerAdminToolsUpdate(
          rowId,
          "status",
          user.name,
          newValue,
          crmSystem,
          tenantId
        );
        console.log("Dealer Admin Tools update Status result :: ", result);
        if (result.data && result.data.affectedRows > 0) {
          console.log("confirm payload : ", confirmPayload);
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.dealerId === rowId
                ? {
                    ...row,
                    status: newValue,
                  }
                : row
            )
          );
        }
      } else if (confirmAction === "STDataPull") {
        console.log("tenantId : ",confirmPayload);
        const result = await dealerAdminToolsUpdate(
          rowId,
          "invoice",
          user.name,
          newValue,
          crmSystem,
          tenantId
        );
        console.log("Dealer Admin Tools update STDataPull result :: ", result);
        if (result.data && result.data.affectedRows > 0) {
          console.log("confirm payload : ", confirmPayload);
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.dealerId === rowId
                ? {
                    ...row,
                    invoiceStatus: newValue,
                  }
                : row
            )
          );
        }
      } else if (confirmAction === "LastSyncDate") {
        const result = await dealerAdminToolsUpdate(
          rowId,
          "lastsyncdate",
          user.name,
          newValue,
          crmSystem,
          tenantId
        );
        console.log(
          "Dealer Admin Tools update LastSyncDate result :: ",
          result
        );
        // Deliberately does NOT write newValue into the row. The old guard here checked
        // result.data.affectedRows, which the handler stopped returning two contract changes
        // ago, so this block was dead and the button gave the operator no signal at all --
        // a 200 and a 500 looked identical, the dialog just closed. Repairing the guard
        // rather than removing it would have put the picked date straight back into the
        // grid's syncEndDate, which is DEFECT 180's symptom: a "Last Sync Date" reflecting
        // what someone typed instead of a sync that succeeded. "Last Synced To" is now
        // advanced by exactly one thing (jbwworkizjobsdatasave's CASE WHEN on 'Success'), so
        // the UI's job is to say the trigger fired and let the next refresh show the real
        // date.
        setNotice({
          severity: "success",
          message: `${result.data?.message ?? "Sync triggered"} — Last Synced To will update once the sync completes successfully.`,
        });
        setLoading(false);
      } else if (confirmAction === "exceptiondays") {
        const result = await dealerAdminToolsUpdate(
          rowId,
          "exceptiondays",
          user.name,
          newValue,
          crmSystem,
          tenantId
        );
        console.log(
          "Dealer Admin Tools update exceptiondays result :: ",
          result
        );
        if (result.data && result.data.affectedRows > 0) {
          console.log("confirm payload : ", confirmPayload);
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.dealerId === rowId
                ? {
                    ...row,
                    claimExceptionDays: newValue,
                  }
                : row
            )
          );
        }
        setLoading(false);
      } else if (confirmAction === "waitingperiod") {
        const result = await dealerAdminToolsUpdate(
          rowId,
          "waitingperiod",
          user.name,
          newValue,
          crmSystem,
          tenantId
        );
        console.log(
          "Dealer Admin Tools update waitingperiod result :: ",
          result
        );
        if (result.data && result.data.affectedRows > 0) {
          console.log("confirm payload : ", confirmPayload);
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.dealerId === rowId
                ? {
                    ...row,
                    claimWaitingPeriod: newValue,
                  }
                : row
            )
          );
        }
        setLoading(false);
      }

      setConfirmOpen(false);
      setConfirmAction(null);
      setConfirmPayload(null);
    } catch (error) {
      console.error("Update failed:", error);
      // Shared by every action in this handler, not just the sync trigger: a failed request
      // used to console.error and close the dialog, which is indistinguishable from success.
      setNotice({ severity: "error", message: describeError(error) });
      setLoading(false);
    } finally {
      setConfirmLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      field: "dealerId",
      headerName: "DealerID",
      width: 75,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "dealerName",
      headerName: "Dealer Name",
      width: 110,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
        {
      field: "tenantId",
      headerName: "TenantID",
      width: 80,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
        {
      field: "tenantName",
      headerName: "Tenant Name",
      width: 110,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "crmSystem",
      headerName: "Platform",
      width: 80,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "date",
      headerName: "Created Date",
      width: 110,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => {
        if (param.value === null) return "";
        return (
          <JBWDateLabel inputDate={param.value} dateFormat="MMM.DD.YYYY" />
        );
      },
    },
    {
      field: "claimExceptionDays",
      headerName: "ExceptionDays",
      width: 110,
      sortable: false,
      headerAlign: "center",
      editable: true,
      type: "number",
      align: "center",
    },
    {
      field: "claimWaitingPeriod",
      headerName: "WaitingPeriod",
      width: 110,
      sortable: false,
      headerAlign: "center",
      editable: true,
      type: "number",
      align: "center",
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: ({ value, row }) => {
        const getBgColor = (val) => {
          switch (val) {
            case "Active":
              return "#4caf50";
            case "InActive":
              return "#d4af37";
            default:
              return "#e0e0e0";
          }
        };

        return (
          <Select
            value={value}
            onChange={(e) =>
              openConfirmation("Status", {
                rowId: row.dealerId,
                newValue: value === "Active" ? "InActive" : "Active",
                crmSystem: row.crmSystem,
                tenantId:row.tenantId
              })
            }
            size="small"
            fullWidth
            sx={{
              backgroundColor: getBgColor(value),
              color: "#fff",
              borderRadius: "20px",
              fontWeight: 500,
              "& .MuiSelect-select": { padding: "6px 12px" },
            }}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: "invoiceStatus",
      headerName: "ST Data Pull",
      width: 130,
      renderCell: ({ value, row }) => {
        const getBgColor = (val) => {
          switch (val) {
            case "Completed":
              return "#2e7d32"; // green
            case "Exported":
              return "#f9a825"; // yellow
            case "Posted":
              return "#0288d1"; // blue
            default:
              return "#e0e0e0"; // gray
          }
        };

        return (
          <Select
            value={value}
            onChange={(e) =>
              openConfirmation("STDataPull", {
                rowId: row.dealerId,
                newValue: e.target.value,
                crmSystem: row.crmSystem,
                tenantId:row.tenantId,
              })
            }
            size="small"
            fullWidth
            sx={{
              backgroundColor: getBgColor(value),
              color: "#fff",
              borderRadius: "20px",
              fontWeight: 500,
              "& .MuiSelect-select": { padding: "6px 12px" },
            }}
          >
            {EXPORT_STATUS_VALUES.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: "autoProcessAgreement",
      headerName: "AutoProcess",
      width: 110,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: ({ value, row }) =>
        autoProcessLoading[row.dealerId] ? (
          <CircularProgress size={20} />
        ) : (
          <JbwSwitch
            setChecked={!!value}
            isChecked={(checked) => {
              setAutoProcessLoading((prev) => ({ ...prev, [row.dealerId]: true }));
              updateAutoProcessAgreement(row.tenantId, checked)
                .then(() => {
                  setRows((prevRows) =>
                    prevRows.map((r) =>
                      r.dealerId === row.dealerId
                        ? { ...r, autoProcessAgreement: checked }
                        : r
                    )
                  );
                })
                .catch((err) => {
                  console.error("AutoProcess update failed:", err);
                })
                .finally(() => {
                  setAutoProcessLoading((prev) => ({ ...prev, [row.dealerId]: false }));
                });
            }}
            id={`autoprocess-${row.dealerId}`}
          />
        ),
    },
    {
      field: "lastSyncedTo",
      headerName: "Last Synced To",
      width: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      // Static, and that is the point. This is vwOnboardedDealers.lastSyncedTo -- the newest
      // syncEndDate among the dealer's SUCCESSFUL tblTransmissionDetailsHistory rows -- so it
      // moves only when a sync actually extended coverage, and it picks that up on the next
      // page load. It used to be the same cell as the picker below, seeded from this value and
      // written back with whatever was typed into it, which is DEFECT 180: a "Last Sync Date"
      // reporting a date someone chose rather than a sync that ran.
      renderCell: ({ value }) =>
        value ? <JBWDateLabel inputDate={value} dateFormat="MMM.DD.YYYY" /> : "Never",
    },
    {
      field: "syncFrom",
      headerName: "Sync From",
      width: 260,
      sortable: false,
      // Left, not centre, and deliberately out of step with every other column here. This one
      // holds TWO controls -- the picker and the Sync button -- so a centred label sits above
      // the midpoint of the pair rather than above the field it names. Left-aligning the
      // header and the cell together puts "Sync From" directly over the date input.
      headerAlign: "left",
      align: "left",
      // maxDate silently greys out the disallowed half of the calendar, so the constraint is
      // invisible until an operator tries to click past it and nothing happens. This says why.
      //
      // Sized to SYNC_PICKER_WIDTH, not the column, so both the label and the icon are placed
      // against the PICKER: the label centred over it, the icon on its right edge. A fixed
      // width here also sidesteps the fact that renderHeader output sits in a shrink-to-fit
      // container, where a percentage width would resolve against the content itself.
      //
      // The icon is absolutely positioned rather than sharing the flex row. In the row it
      // would take up space and push the label off centre by half its width -- which is the
      // thing this header is being aligned for.
      renderHeader: () => (
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={SYNC_PICKER_WIDTH}
        >
          <span>Sync From</span>
          <Tooltip
            title={
              <div style={{ whiteSpace: "pre-line" }}>
                {"Dates after Last Synced To are disabled.\n\n" +
                  "Syncing from a later date would leave the span in between uncollected, " +
                  "and nothing in the system can move the sync date backwards to recover it.\n\n" +
                  "Pick an earlier date to re-pull a window that was already synced."}
              </div>
            }
          >
            <InfoRoundedIcon
              sx={{ position: "absolute", right: 0, fontSize: 16, opacity: 0.6 }}
            />
          </Tooltip>
        </Box>
      ),
      renderCell: ({ row }) => {
        // The lower bound for a manual sync, and nothing else -- this value is never stored in
        // any column. It defaults to the dealer's last successful synced-to date so an
        // ordinary click resumes exactly where the last sync finished and leaves no gap;
        // picking an earlier date asks for a backfill, which is the reason the field is
        // editable at all. Today is the fallback for a dealer that has never synced, since
        // there is no cursor to resume from.
        // Parsed in UTC deliberately. lastSyncedTo arrives as an ISO "...Z" string, and the
        // cell directly above renders it with moment.utc -- so parsing it here in LOCAL time
        // made the two adjacent cells disagree by a day for any cursor whose UTC time is
        // earlier than the viewer's offset (most overnight batch timestamps, for anyone west
        // of UTC). The grid would show "Last Synced To: Jan.06" while this picker defaulted to
        // Jan 5 and maxDate greyed out Jan 6, so the operator could not pick the date the row
        // was displaying and the tooltip's "dates after Last Synced To are disabled" was false
        // by one day.
        const fallback = row.lastSyncedTo ? dayjs.utc(row.lastSyncedTo) : dayjs.utc();
        const selected = datePickerValues[row.dealerId] ?? fallback;

        return (
          <Box display="flex" alignItems="center" gap={1} width="100%">
            {/* Pinned to the same width the header's icon is positioned against, and the
                TextField is fullWidth so the input FILLS it -- otherwise the picker takes its
                natural width and the icon above lines up with nothing. */}
            <Box width={SYNC_PICKER_WIDTH} flexShrink={0}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={selected}
                  onChange={(newDate) =>
                    setDatePickerValues((prev) => ({
                      ...prev,
                      [row.dealerId]: newDate,
                    }))
                  }
                  format="YYYY-MM-DD"
                  // Forward of the cursor is the one choice that can lose data: that window
                  // succeeds, reports no jobs, and leaves the span between the cursor and the
                  // chosen start uncollected. jbwworkizjobsdatasave refuses to advance the
                  // cursor in that case, so nothing is lost either way -- but the sync also
                  // achieves nothing, so it is better not to offer it. Today is the ceiling for
                  // a dealer with no cursor yet.
                  maxDate={fallback}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "50px",
                        },
                        "& .MuiInputBase-input": {
                          height: "1.4375em",
                          padding: "5px 14px",
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
            <Button
              variant="text"
              size="small"
              onClick={() =>
                openConfirmation("LastSyncDate", {
                  rowId: row.dealerId,
                  // Date only. The handler and both callees validate against
                  // /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}:\d{2})?$/ before this reaches the
                  // Workiz job/all URL, so the format is a contract, not a preference.
                  newValue: selected.format("YYYY-MM-DD"),
                  crmSystem: row.crmSystem,
                  tenantId:row.tenantId,
                })
              }
            >
              Sync
            </Button>
          </Box>
        );
      },
    },
  ], [openConfirmation, datePickerValues, autoProcessLoading]);

  const QuickSearchToolbar = () => {
    return (
      <Box
        sx={{
          p: 0.5,
          pb: 0,
        }}
      >
        <GridToolbarQuickFilter />
      </Box>
    );
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <AdminDealerSearch
          onSelect={(value, allDealersData) => {
            setAllDealersData(allDealersData);
            if (value && !allDealersData) {
              setRows([value]);
              setNoResults(false);
            } else {
              setRows([value]);
              setNoResults(true);
            }
          }}
        />
      </Box>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          {confirmAction === "Status" && (
            <>
              Changing the Status will effect all the tenants.
              <br />
              Are you sure you want to update the Status?
            </>
          )}

          {confirmAction === "STDataPull" && (
            <>Are you sure you want to update the Invoice Status?</>
          )}

          {confirmAction === "LastSyncDate" && (
            <>
              This will sync jobs updated since{" "}
              <strong>{confirmPayload?.newValue}</strong>.
              <br />
              Are you sure you want to start the sync?
            </>
          )}
          {confirmAction === "exceptiondays" && (
            <>Are you sure you want to update the Claim Exception Date?</>
          )}
          {confirmAction === "waitingperiod" && (
            <>Are you sure you want to update the Claim Waiting Period?</>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            color="inherit"
            disabled={confirmLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color="primary"
            variant="contained"
            disabled={confirmLoading}
            startIcon={
              confirmLoading && <CircularProgress size={20} color="inherit" />
            }
          >
            {confirmLoading ? "Updating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {noResults ? (
        !allDealersData ? (
          <Typography variant="body1" color="red">
            No Dealer found with the give dealer Id.
          </Typography>
        ) : (
          <Typography variant="body1" color="red">
            Dealer is not onboarded.Please onboard the Dealer using the New
            OnBoarding Process
          </Typography>
        )
      ) : (
        <Box sx={{ height: 400 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => `${row.dealerId}`}
            processRowUpdate={(newRow, oldRow, fields) => {
              console.log("new row : ", newRow);
              console.log("Old row : ", oldRow);
              if (oldRow.claimExceptionDays !== newRow.claimExceptionDays) {
                openConfirmation("exceptiondays", {
                  rowId: newRow.dealerId,
                  newValue: newRow.claimExceptionDays,
                  crmSystem: newRow.crmSystem,
                  tenantId:newRow.tenantId,
                });
                const updateRow = {
                  ...newRow,
                }; // Update the row state to 'updated'.

                return updateRow;
              } else if (
                oldRow.claimWaitingPeriod !== newRow.claimWaitingPeriod
              ) {
                openConfirmation("waitingperiod", {
                  rowId: newRow.dealerId,
                  newValue: newRow.claimWaitingPeriod,
                  crmSystem: newRow.crmSystem,
                  tenantId:newRow.tenantId,
                });
                const updateRow = {
                  ...newRow,
                }; // Update the row state to 'updated'.

                return updateRow;
              }

              const updateRow = {
                ...newRow,
              }; // Update the row state to 'updated'.

              return updateRow;
            }}
            onProcessRowUpdateError={(error) => {
              console.log("error processing cell :", error);
            }}
            disableColumnFilter={true}
            disableColumnMenu={true}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              toolbar: QuickSearchToolbar,
            }}
          />
        </Box>
      )}
      <Snackbar
        open={!!notice}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={notice?.severity ?? "info"} onClose={() => setNotice(null)}>
          {notice?.message}
        </Alert>
      </Snackbar>
    </>
  );
};
